/**
 * Mapeo de enums del API GET .../website → claves internas de la app.
 * @see respuesta `data`: header, portrait, footer, vehicleFinder, vehicleDetail, reviews, menu
 */

export const WEBSITE_HEADER_MAP = {
  HEADER_CLASSIC: 'header-horizontal',
  HEADER_HORIZONTAL: 'header-horizontal',
  HEADER_DEFAULT: 'header-horizontal',
  HEADER_CENTERED: 'header-centered',
  HEADER_STACKED: 'header-stacked',
  HEADER_LEFT: 'header-stacked',
}

export const WEBSITE_HERO_MAP = {
  HERO_VIDEO: 'hero-centered',
  HERO_CENTERED: 'hero-centered',
  HERO_SPLIT_LEFT: 'hero-split-left',
  HERO_SPLIT_RIGHT: 'hero-split-right',
  HERO_GRID: 'hero-grid',
}

export const WEBSITE_FOOTER_MAP = {
  FOOTER_DEFAULT: 'footer-centered-light',
  FOOTER_SIMPLE: 'footer-centered-light',
  FOOTER_CENTERED_LIGHT: 'footer-centered-light',
  FOOTER_CENTERED_DARK: 'footer-centered-dark',
  FOOTER_COLUMNS: 'footer-columns-light',
  FOOTER_COLUMNS_LIGHT: 'footer-columns-light',
  FOOTER_COLUMNS_DARK: 'footer-columns-dark',
}

export const WEBSITE_LISTINGS_MAP = {
  FINDER_ADVANCED: 'listings-pills',
  FINDER_PILLS: 'listings-pills',
  FINDER_SIDEBAR: 'listings-sidebar',
  FINDER_COMPACT: 'listings-compact',
}

export const WEBSITE_VEHICLE_DETAIL_MAP = {
  VEHICLE_DETAIL_DEFAULT: 'vehicle-detail-v2',
  VEHICLE_DETAIL_V1: 'vehicle-detail-v1',
  VEHICLE_DETAIL_CENTERED: 'vehicle-detail-v1',
  VEHICLE_DETAIL_V2: 'vehicle-detail-v2',
  VEHICLE_DETAIL_FULLSCREEN: 'vehicle-detail-v2',
}

export const WEBSITE_REVIEWS_MAP = {
  REVIEWS_GRID: 'reviews-grid',
  REVIEWS_LIST: 'reviews-list',
  REVIEWS_CAROUSEL: 'reviews-list',
}

function mapEnum(apiValue, map, fallback) {
  if (apiValue == null || String(apiValue).trim() === '') return fallback
  const key = String(apiValue).trim()
  return map[key] ?? map[key.toUpperCase()] ?? fallback
}

const COMPONENT_TYPE_MAPS = {
  header: { map: WEBSITE_HEADER_MAP, fallback: 'header-horizontal', layoutKey: 'header' },
  footer: { map: WEBSITE_FOOTER_MAP, fallback: 'footer-centered-light', layoutKey: 'footer' },
  hero: { map: WEBSITE_HERO_MAP, fallback: 'hero-centered', layoutKey: 'portrait' },
  listings: { map: WEBSITE_LISTINGS_MAP, fallback: 'listings-pills', layoutKey: 'vehicleFinder' },
  vehicleDetail: { map: WEBSITE_VEHICLE_DETAIL_MAP, fallback: 'vehicle-detail-v2', layoutKey: 'vehicleDetail' },
  reviews: { map: WEBSITE_REVIEWS_MAP, fallback: 'reviews-grid', layoutKey: 'reviews' },
}

/** Resuelve clave interna de componente desde config mergeada o enums crudos del API. */
export function resolveWebsiteComponentKey(type, config) {
  const mapped = config?.components?.[type]
  if (mapped) return mapped

  const layout = config?.websiteLayout
  const def = COMPONENT_TYPE_MAPS[type]
  if (!def) return 'default'

  const raw = layout?.[def.layoutKey]
  if (raw != null && String(raw).trim() !== '') {
    return mapEnum(raw, def.map, def.fallback)
  }

  return def.fallback
}

export function mapWebsiteApiToConfig(data) {
  if (data == null || typeof data !== 'object') return {}

  const hasMenu = data.menu != null && Array.isArray(data.menu.items)
  const menuItems = hasMenu
    ? data.menu.items
        .map((item) => {
          if (!item?.href) return null
          return {
            href: String(item.href),
            label: item.label != null ? String(item.label) : undefined,
            openInNewTab: Boolean(item.openInNewTab),
          }
        })
        .filter(Boolean)
    : null

  const components = {
    header: mapEnum(data.header, WEBSITE_HEADER_MAP, 'header-horizontal'),
    hero: mapEnum(data.portrait, WEBSITE_HERO_MAP, 'hero-centered'),
    footer: mapEnum(data.footer, WEBSITE_FOOTER_MAP, 'footer-centered-light'),
    listings: mapEnum(data.vehicleFinder, WEBSITE_LISTINGS_MAP, 'listings-pills'),
    vehicleDetail: mapEnum(data.vehicleDetail, WEBSITE_VEHICLE_DETAIL_MAP, 'vehicle-detail-v2'),
    reviews: mapEnum(data.reviews, WEBSITE_REVIEWS_MAP, 'reviews-grid'),
  }

  return {
    tenant: data.tenantUuid || undefined,
    subdomain: data.subdomain ? String(data.subdomain).trim() : undefined,
    headerNav: hasMenu ? menuItems : undefined,
    footerNav: hasMenu ? menuItems : undefined,
    components,
    websiteLayout: {
      header: data.header ?? null,
      portrait: data.portrait ?? null,
      footer: data.footer ?? null,
      vehicleFinder: data.vehicleFinder ?? null,
      vehicleDetail: data.vehicleDetail ?? null,
      reviews: data.reviews ?? null,
    },
    website: data,
  }
}
